import { Body1, Button, Spinner, Title2 } from '@fluentui/react-components';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { AppError } from '../../api/errors.js';
import { useHttpClient } from '../../api/HttpClientContext.js';
import { config } from '../../config.js';
import { useWorkspaceContext } from '../../context/WorkspaceContext.js';
import {
  AskAgency,
  hostedReportHtml,
} from '../../features/report-agency/index.js';
import { ReportFrame } from '../../features/report-trajectory/index.js';
import type { EvaluationRun } from '../../services/workflowApi.interface.js';
import { WorkflowApiService } from '../../services/workflowApi.js';

import { useStyles } from './EvalReportPage.style.js';

/**
 * EvalReportPage — run-detail view for a single evaluation run.
 *
 * Fetches the run first, then branches on `runState`:
 *   - completed + report loads → iframe with the server-generated HTML
 *   - failed                   → error panel with errorMessage + errorDetails
 *   - completed + report 404   → raw-result download (if blob present) or
 *                                "result missing" message (if blob 404s too)
 *   - any other state          → generic "Report unavailable" placeholder
 *
 * The iframe uses `srcDoc` so the authenticated fetch retrieves the HTML;
 * ReportFrame keeps a script-only sandbox and validates trajectory requests
 * before opening a parent-owned tab or showing a fallback link. The report
 * cannot open popups, access the parent's origin, submit forms, or navigate
 * the top-level page.
 */
type LoadState =
  | { kind: 'loading' }
  | { kind: 'notFound' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; run: EvaluationRun; html: string | null }
  // Report endpoint returned 404. `rawResult` is the JSON blob if present,
  // or null if `getEvaluationRunResult` also 404'd (blob missing entirely).
  | { kind: 'reportMissing'; run: EvaluationRun; rawResult: unknown };

// Hand bytes to the browser via a blob: URL so the SPA dev server doesn't
// intercept the path and serve index.html. Same pattern as RawJsonViewer.
function downloadJson(value: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(value, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function EvalReportPage() {
  const { workspaceName, id } = useParams<{
    workspaceName: string;
    id: string;
  }>();
  const httpClient = useHttpClient();
  const styles = useStyles();
  const navigate = useNavigate();
  const { activeName } = useWorkspaceContext();

  const runsListPath = workspaceName
    ? `/workspaces/${encodeURIComponent(workspaceName)}/eval-runs`
    : '/';

  // When the user switches workspace via the switcher while on this page,
  // redirect to the new workspace's eval-runs URL.
  useEffect(() => {
    if (activeName && workspaceName && activeName !== workspaceName) {
      void navigate(`/workspaces/${encodeURIComponent(activeName)}/eval-runs`, {
        replace: true,
      });
    }
  }, [activeName, workspaceName, navigate]);

  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    if (!workspaceName || !id) {
      setState({
        kind: 'error',
        message: 'Missing workspace name or run ID.',
      });
      return;
    }

    const service = new WorkflowApiService(httpClient);
    let cancelled = false;

    void (async () => {
      setState({ kind: 'loading' });

      let run: EvaluationRun;
      try {
        run = await service.getEvaluationRun(workspaceName, id);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof AppError && err.statusCode === 404) {
          setState({ kind: 'notFound' });
        } else {
          setState({
            kind: 'error',
            message: err instanceof Error ? err.message : 'Failed to load run.',
          });
        }
        return;
      }

      if (run.runState !== 'completed') {
        if (!cancelled) setState({ kind: 'ready', run, html: null });
        return;
      }

      try {
        const html = await service.getEvaluationReport(workspaceName, [id]);
        if (!cancelled) {
          setState({ kind: 'ready', run, html: hostedReportHtml(html) });
        }
      } catch (err) {
        if (cancelled) return;
        // 404 on the report can mean the result blob is missing OR present
        // but unrenderable. Probe the raw-result endpoint to tell them apart;
        // getEvaluationRunResult returns null on its own 404 but still throws
        // on other errors — handle those so the page doesn't get stuck in the
        // loading state.
        if (err instanceof AppError && err.statusCode === 404) {
          try {
            const rawResult = await service.getEvaluationRunResult(
              workspaceName,
              id,
            );
            if (!cancelled) {
              setState({ kind: 'reportMissing', run, rawResult });
            }
          } catch (rawErr) {
            if (cancelled) return;
            setState({
              kind: 'error',
              message:
                rawErr instanceof Error
                  ? rawErr.message
                  : 'Failed to load raw result.',
            });
          }
          return;
        }
        setState({
          kind: 'error',
          message:
            err instanceof Error ? err.message : 'Failed to load report.',
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [httpClient, workspaceName, id]);

  if (state.kind === 'loading') {
    return (
      <div className={styles.centered}>
        <Spinner label="Loading…" />
      </div>
    );
  }

  if (state.kind === 'notFound') {
    return (
      <div className={styles.centered}>
        <Title2>Run not found</Title2>
        <Body1>No evaluation run exists with ID {id}.</Body1>
        <Button onClick={() => void navigate(runsListPath)}>
          Back to runs
        </Button>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className={styles.centered}>
        <Title2>Report unavailable</Title2>
        <Body1>{state.message}</Body1>
        <Button onClick={() => void navigate(runsListPath)}>
          Back to runs
        </Button>
      </div>
    );
  }

  if (state.kind === 'reportMissing') {
    const { run, rawResult } = state;

    if (rawResult != null) {
      return (
        <div className={styles.centered}>
          <Title2>Report not available</Title2>
          <Body1>
            This run completed, but its result file couldn&apos;t be rendered as
            a report. You can still download the raw JSON.
          </Body1>
          <Button
            appearance="primary"
            onClick={() =>
              downloadJson(rawResult, `evaluation-run-${run.id}.json`)
            }
          >
            Download raw result (JSON)
          </Button>
          <Button onClick={() => void navigate(runsListPath)}>
            Back to runs
          </Button>
        </div>
      );
    }

    return (
      <div className={styles.centered}>
        <Title2>Result file is missing</Title2>
        <Body1>
          No result was recorded for this run. Run ID: <code>{run.id}</code>
        </Body1>
        <Button onClick={() => void navigate(runsListPath)}>
          Back to runs
        </Button>
      </div>
    );
  }

  const { run, html } = state;

  if (run.runState === 'failed') {
    return (
      <div className={styles.centered}>
        <Title2>Run failed</Title2>
        <Body1>{run.errorMessage ?? 'No error message was recorded.'}</Body1>
        {run.errorDetails != null && (
          <details>
            <summary>Details</summary>
            <pre>{JSON.stringify(run.errorDetails, null, 2)}</pre>
          </details>
        )}
        <Button onClick={() => void navigate(runsListPath)}>
          Back to runs
        </Button>
      </div>
    );
  }

  if (run.runState === 'completed' && html != null) {
    return (
      <div className={styles.root}>
        <AskAgency
          key={`${workspaceName}/${id}`}
          source={{
            workspace: workspaceName ?? '',
            runId: id ?? '',
            apiBaseUrl: config.agencyApiBaseUrl,
            origin: window.location.origin,
            serviceAppId: config.workflowApiServiceAppId,
            tenantId: config.entraTenantId,
          }}
        />
        <ReportFrame
          className={styles.iframe}
          title="Evaluation Report"
          html={html}
        />
      </div>
    );
  }

  // Active states: show a spinner so users see "wait, work in progress"
  // instead of an error-looking placeholder.
  if (
    run.runState === 'pending' ||
    run.runState === 'running' ||
    run.runState === 'cancelling'
  ) {
    return (
      <div className={styles.centered}>
        <Spinner label={`Run is ${run.runState}…`} />
        <Button onClick={() => void navigate(runsListPath)}>
          Back to runs
        </Button>
      </div>
    );
  }

  // cancelled / completed-without-html
  return (
    <div className={styles.centered}>
      <Title2>Report unavailable</Title2>
      <Body1>Run is {run.runState}.</Body1>
      <Button onClick={() => void navigate(runsListPath)}>Back to runs</Button>
    </div>
  );
}
